// @flow

import React, { Component } from 'react';
import {
  Field,
  reduxForm,
} from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import {
  LabeledTextarea as ReduxFormLabeledTextarea,
  ReduxFormLabeledInput,
  LabeledField,
  ReduxFormLabeledSelect,
} from '../form/labeledFields';
import AlternativesModal from './components/alternativesModal';
import {
  email,
  equalTo,
  phone,
  required,
} from '../../helpers/formValidators';
import DateField from '../formidable/fields/date';
import Button from '../actionButton';
import Address from './components/address';
import {
  ReduxFormRadioGroup,
  ReduxFormCheckbox,
  Error,
} from '../form';
import {
  initForm,
  fetchAddress,
  checkStock,
  submitForm,
  alternativesOverlayClose,
} from '../../store/orderForm/actions';
import {
  Wrapper,
  SubTitle,
  Form,
  AgreeBox,
  AgreeOption,
  ComfortText,
} from './style';
import getCookie from '../../helpers/cookie/cookie';
import {
  getLabels,
  hasShops,
} from '../../store/formSettings/selectors/main';
import {
  getIsLoading,
  getIsShowingAlternativesOverlay,
} from '../../store/orderForm/selectors';
import {
  getRequired,
  getEmail,
  getNoSelection,
  getPhone,
  getDate,
  getEmailEqual,
  getNotSend,
} from '../../store/formSettings/selectors/errorMessages';
import { getActivePdbId } from '../../store/pdb';
import { onCheckoutStep } from '../../store/gtm';
import type {
  DispatchProps,
  OwnProps,
  Props,
  StateProps,
} from './types';

class OrderForm extends Component<Props> {
  validators: Object;

  provinces: Array<string>;

  constructor(props) {
    super(props);

    this.validators = {
      required: required(props.errorMessages.required),
      emailEqual: equalTo(props.errorMessages.emailEqual, 'email'),
      emailValid: email(props.errorMessages.email),
      phone: phone(props.errorMessages.phone),
    };
  }

  componentDidMount() {
    // Currently the orderpage is always on step 4, this may be subject to change in the future.
    const { initializeForm, onContainerAppear } = this.props;
    initializeForm();
    onContainerAppear(4);
  }

  onClose = () => {
    const { alternativesModalClose, handleSubmit } = this.props;
    alternativesModalClose();
    handleSubmit(this.checkOnSubmit)();
  }

  checkOnSubmit = (values: Object) => {
    const { checkModelStock } = this.props;
    // Check this stock before form submission.
    checkModelStock(values);
  }

  checkPastingEmail = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 86 && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
    }
  };

  render() {
    const {
      error,
      errorMessages,
      handleSubmit,
      labels,
      fetchModelAddress,
      isLoading,
      isShowingAlternativesOverlay,
      activePdbId,
    } = this.props;

    // Only destruct labels that are used multiple times.
    const {
      select_first_option_label,
      income_sources,
    } = labels;

    return (
      <Wrapper>
        <SubTitle>{labels.form_title}</SubTitle>
        <Form onSubmit={handleSubmit(this.checkOnSubmit)} data-cy="orderForm">
          {hasShops && getCookie('shop') && labels.shops && (
            <Field
              component={ReduxFormLabeledSelect}
              name="referrer"
              type="select"
              label={labels.shop_label}
              validate={[this.validators.required]}
            >
              <option value="none">{select_first_option_label}</option>
              {labels.shops.map(({ shop }) => <option key={shop} value={shop}>{shop}</option>)}
            </Field>
          )}
          <Field
            component={ReduxFormLabeledSelect}
            name="income_source"
            type="select"
            label={labels.income_source_label}
            validate={[this.validators.required]}
          >
            <option value="">{select_first_option_label}</option>
            {Object.keys(income_sources).map(key => (
              <option key={key} value={key}>
                {income_sources[key]}
              </option>
            ))}
          </Field>
          <LabeledField label={labels.salutation_label}>
            <Field
              component={ReduxFormRadioGroup}
              name="salutation"
              validate={[this.validators.required]}
              options={[
                { label: labels.salutation_female, value: 'mvr' },
                { label: labels.salutation_male, value: 'dhr' },
              ]}
            />
          </LabeledField>
          <Field
            component={ReduxFormLabeledInput}
            name="initials"
            type="text"
            label={labels.initials}
            validate={[this.validators.required]}
          />
          <Field
            component={ReduxFormLabeledInput}
            name="first_name"
            type="text"
            label={labels.first_name_label}
            validate={[this.validators.required]}
          />
          <Field
            component={ReduxFormLabeledInput}
            name="last_name"
            type="text"
            label={labels.last_name_label}
            validate={[this.validators.required]}
          />
          <DateField
            name="date_of_birth"
            label={labels.date_of_birth_label}
            endYear={labels.datum_from}
            startYear={labels.datum_to}
          />
          <Address
            fetchAddress={fetchModelAddress}
            requiredErrorMessage={errorMessages.required}
            label={labels.postal_code_and_house_number_label}
            postalCodePlaceholder={labels.postal_code_placeholder}
            houseNumberPlaceholder={labels.house_number_placeholder}
            houseNumberSuffixPlaceholder={
              labels.house_number_suffix_placeholder
            }
            streetLabel={labels.street_label}
            cityLabel={labels.city_label}
          />
          {labels.provinces.length > 0 && (
            <Field
              component={ReduxFormLabeledSelect}
              name="province"
              type="select"
              label={labels.province_label}
              validate={[this.validators.required]}
            >
              <option value="">{select_first_option_label}</option>
              {labels.provinces.map(province => (
                <option key={province} value={province}>
                  {province}
                </option>
              ))}
            </Field>
          )}
          <Field
            component={ReduxFormLabeledInput}
            name="telephone"
            type="number"
            label={labels.telephone_label}
            validate={[this.validators.required, this.validators.phone]}
          />
          <Field
            component={ReduxFormLabeledInput}
            name="email"
            type="email"
            label={labels.email_label}
            placeholder={labels.email_placeholder}
            validate={[this.validators.required, this.validators.emailValid]}
          />
          <Field
            component={ReduxFormLabeledInput}
            name="email-check"
            type="email"
            label={labels.email_check_label}
            placeholder={labels.email_check_placeholder}
            validate={[
              this.validators.required,
              this.validators.emailEqual,
              this.validators.emailValid,
            ]}
            onKeyDown={this.checkPastingEmail}
          />
          {process.env.LISTO_SHOW_NOTES_IN_ORDER_FORM === 'true' && (
            <Field
              component={ReduxFormLabeledTextarea}
              name="note_client"
              label={labels.comment_label}
              placeholder={labels.comment_placeholder}
            />
          )}
          <AgreeBox>
            <AgreeOption>
              <Field
                component={ReduxFormCheckbox}
                name="terms"
                type="checkbox"
                validate={[this.validators.required]}
              />
              {(labels.agree_terms_text && labels.agree_terms_text !== '')
                ? <span dangerouslySetInnerHTML={{ __html: labels.agree_terms_text }} /> /* eslint-disable-line */
                : (
                  <span>
                    {labels.terms_label}
                    &nbsp;
                    <a href={labels.terms_link} rel="noopener noreferrer" target="_blank">
                      {labels.terms_link_text}
                    </a>
                    .
                  </span>
                )}
            </AgreeOption>
            {process.env.LISTO_SHOW_NEWSLETTER_IN_ORDERFORM === 'true' && (
              <AgreeOption>
                <Field
                  component={ReduxFormCheckbox}
                  name="newsletter"
                  type="checkbox"
                />
                <span>{labels.newsletter_text}</span>
              </AgreeOption>
            )}
          </AgreeBox>
          <Button
            disabled={isLoading}
            loading={isLoading}
            caret
            variant="default"
            type="submit"
          >
            {labels.submit_button_label}
          </Button>
          {error && (
            <Error general message={errorMessages.notSend} />
          )}
          <ComfortText>{labels.comfort_message}</ComfortText>
        </Form>
        {isShowingAlternativesOverlay
          && <AlternativesModal
            pdbId={activePdbId}
            onClose={this.onClose}
          />}
      </Wrapper>
    );
  }
}

const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
  initializeForm: () => dispatch(initForm()),
  fetchModelAddress: form => dispatch(fetchAddress(form)),
  onContainerAppear: (step: number) => dispatch(onCheckoutStep(step)),
  checkModelStock: (values: Object) => dispatch(checkStock(values)),
  redirectToConfig: (configLink: string) => dispatch(push(configLink)),
  submitForm: (values: Object) => dispatch(submitForm(values)),
  alternativesModalClose: () => dispatch(alternativesOverlayClose()),
});

const mapStateToProps = (state): StateProps => ({
  isLoading: getIsLoading(state),
  isShowingAlternativesOverlay: getIsShowingAlternativesOverlay(state),
  activePdbId: getActivePdbId(state),
  errorMessages: {
    required: getRequired(state),
    email: getEmail(state),
    noSelection: getNoSelection(state),
    phone: getPhone(state),
    date: getDate(state),
    emailEqual: getEmailEqual(state),
    notSend: getNotSend(state),
  },
  hasShops: hasShops(state),
  labels: getLabels(state),
});

export default compose(
  connect<Props, OwnProps, StateProps, DispatchProps, any, any>(
    mapStateToProps,
    mapDispatchToProps,
  ),
  reduxForm({
    form: 'order',
    destroyOnUnmount: false,
  }),
)(OrderForm);
